import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  SalesTaxConfig,
  SalesTaxExemption,
  SalesTaxExemptionService,
  SalesTaxExemptionStatus,
  SalesTaxExemptionType,
  SalesTaxQueueItem,
  SalesTaxReviewWarning,
} from '../../service-data/sales-tax-exemption.service';
import { LanguagePipe } from '../../pipe/language.pipe';

/**
 * Reviewer-side panel for managing the Sales Tax Exemption review queue
 * for a single ServiceProvider.
 *
 * Hosted by both:
 * - `NetworkSettingsComponent` (Network role; SP picker drives `serviceProviderId`)
 * - `ServiceProviderSettingsComponent` (SP role; `serviceProviderId` fixed
 *   to the caller's SP via `startupService.selectedRoleId`)
 *
 * Self-contained: fetches the SP's salesTaxConfig + the review queue,
 * lets the reviewer click into individual exemptions, edit accepted
 * states/type, and POST review actions
 * (ACCEPT / REJECT / REQUEST_INFO / REVOKE). All errors render inline.
 */
@Component({
  selector: 'app-sales-tax-review',
  templateUrl: './sales-tax-review.component.html',
  styleUrls: ['./sales-tax-review.component.css'],
})
export class SalesTaxReviewComponent implements OnInit, OnChanges {
  @Input() serviceProviderId: number | null = null;

  salesTaxConfig: SalesTaxConfig | null = null;
  queue: SalesTaxQueueItem[] = [];
  queueLoading = false;
  queueError = '';

  selectedItem: SalesTaxQueueItem | null = null;
  selectedExemption: SalesTaxExemption | null = null;
  selectedLoading = false;
  selectedError = '';

  reviewerAcceptedStates: string[] = [];
  reviewerAcceptedType: 'wholesale' | 'other' = 'other';
  reviewerMessage = '';
  reviewerActing = false;
  reviewerWarnings: SalesTaxReviewWarning[] = [];

  constructor(private salesTaxExemptionService: SalesTaxExemptionService) {}

  ngOnInit() {
    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.serviceProviderId) {
      this.salesTaxConfig = null;
      this.queue = [];
      this.selectedItem = null;
      this.selectedExemption = null;
      this.refresh();
    }
  }

  refresh(): void {
    if (this.serviceProviderId == null) { return; }
    const spId = this.serviceProviderId;
    this.salesTaxExemptionService.getSpConfig(spId).subscribe(
      cfg => { this.salesTaxConfig = cfg; },
      err => {
        // Non-fatal — the matrix will degrade.
        console.warn('SalesTaxReview: getSpConfig failed', err);
      }
    );
    this.refreshQueue();
  }

  refreshQueue(): void {
    if (this.serviceProviderId == null) { return; }
    this.queueLoading = true;
    this.queueError = '';
    this.salesTaxExemptionService.getReviewQueue(this.serviceProviderId,
      ['PENDING_REVIEW', 'INFO_REQUESTED']).subscribe(
      items => {
        this.queue = items || [];
        this.queueLoading = false;
      },
      err => {
        console.error('SalesTaxReview: getReviewQueue failed', err);
        this.queueLoading = false;
        this.queueError = this.errorMessage(err,
          LanguagePipe.get('Failed to load exemption review queue.'));
      }
    );
  }

  open(item: SalesTaxQueueItem): void {
    this.selectedItem = item;
    this.selectedExemption = null;
    this.selectedError = '';
    this.selectedLoading = true;
    this.reviewerWarnings = [];
    this.reviewerMessage = '';
    this.salesTaxExemptionService.getExemption(item.orgId).subscribe(
      ex => {
        this.selectedExemption = ex;
        this.selectedLoading = false;
        this.reviewerAcceptedStates = ex ? (ex.exemptStates || []).slice() : [];
        this.reviewerAcceptedType = (ex && ex.type !== 'non-exempt')
          ? (ex.type as 'wholesale' | 'other') : 'other';
      },
      err => {
        console.error('SalesTaxReview: openExemption failed', err);
        this.selectedLoading = false;
        this.selectedError = this.errorMessage(err,
          LanguagePipe.get('Failed to load exemption details.'));
      }
    );
  }

  close(): void {
    this.selectedItem = null;
    this.selectedExemption = null;
    this.selectedError = '';
    this.reviewerWarnings = [];
  }

  act(action: 'ACCEPT' | 'REJECT' | 'REQUEST_INFO' | 'REVOKE'): void {
    if (!this.selectedItem) { return; }
    this.reviewerActing = true;
    this.reviewerWarnings = [];
    this.selectedError = '';
    const body: any = {
      action,
      message: this.reviewerMessage || undefined,
    };
    if (action === 'ACCEPT') {
      body.acceptedType = this.reviewerAcceptedType;
      body.acceptedStates = this.reviewerAcceptedStates;
    }
    this.salesTaxExemptionService.review(this.selectedItem.orgId, body).subscribe(
      resp => {
        this.reviewerActing = false;
        this.selectedExemption = resp ? resp.exemption : null;
        this.reviewerWarnings = (resp && resp.warnings) ? resp.warnings : [];
        this.refreshQueue();
      },
      err => {
        console.error(`SalesTaxReview: review ${action} failed`, err);
        this.reviewerActing = false;
        this.selectedError = this.errorMessage(err,
          LanguagePipe.get('Review action failed.'));
      }
    );
  }

  openCertificate(): void {
    if (this.selectedExemption && this.selectedExemption.certificateUrl) {
      window.open(this.selectedExemption.certificateUrl, '_blank');
      return;
    }
    if (!this.selectedItem) { return; }
    this.salesTaxExemptionService.requestDownloadUrl(this.selectedItem.orgId).subscribe(
      r => { if (r && r.downloadUrl) { window.open(r.downloadUrl, '_blank'); } },
      err => {
        console.error('SalesTaxReview: requestDownloadUrl failed', err);
        this.selectedError = this.errorMessage(err,
          LanguagePipe.get('Failed to obtain certificate download URL.'));
      }
    );
  }

  isCertificateImage(): boolean {
    const mt = this.selectedExemption && this.selectedExemption.certificate
      && this.selectedExemption.certificate.fileMimeType;
    return !!(mt && mt.indexOf('image/') === 0);
  }

  statusLabel(status: SalesTaxExemptionStatus): string {
    return LanguagePipe.get(status);
  }

  typeLabel(type: SalesTaxExemptionType): string {
    return LanguagePipe.get(type);
  }

  private errorMessage(err: any, fallback: string): string {
    if (err && err.error && typeof err.error === 'object' && err.error.error) {
      return String(err.error.error);
    }
    if (err && err.message) { return String(err.message); }
    return fallback;
  }
}

